import React, { FC, useState } from 'react';
import classNames from 'classnames/bind';
import SlaOptionCard from './components/SlaOptionCard';
import properties from '../../../modules/ajio-util/properties';
import { SlaCodeLabels, SlaOption, SlaViewModel } from './types';
import styles from './index.pcss';

const cx = classNames.bind(styles);

const imageBaseUrl = properties.getProperty('imageBaseUrl');
/** Storefront glyph for the optional seller row. */
const SELLER_ICON = `${imageBaseUrl}/static/img/b2c-seller-icon.svg`;
const SELLER_ICON_LUXE = `${imageBaseUrl}/static/img/b2c-seller-icon-luxe.svg`;

export interface DeliverySLAProps {
  viewModel: SlaViewModel;
  loading?: boolean;
  error?: boolean;
  activePincode?: string;
  /** open the change-pincode flow */
  onChangePincode?: () => void;
  /** fired when the user picks a different SLA option (multi only) */
  onSelectOption?: (option: SlaOption) => void;
  footerNote?: string;
  className?: string;
  /** luxe theme */
  luxe?: boolean;
  /** hide the pincode / change-pincode header row (host supplies its own heading) */
  hideHeader?: boolean;
  /**
   * Host-supplied heading, rendered only once there is a response to head —
   * i.e. with the options or the error, never over the loading skeleton.
   */
  title?: React.ReactNode;
  /** SLA-code → copy map from priorityDeliverySLPTagsConfig (PLP_BUCKET) */
  slaCodeLabels?: SlaCodeLabels;
  /** Cart only — suffix the amount with "/ pc" (prices there are per piece). */
  perPiece?: boolean;
  /**
   * Seller row under the cards ("Seller: <name>"). Omitted by the multiseller
   * hosts, which do not surface the seller here.
   */
  sellerName?: string;
  /** Name the seller on each card — All Sellers only (see SlaOptionCard). */
  showSellerName?: boolean;
  /**
   * Link under the seller row ("Other sellers available"). Omitted by the
   * multiseller hosts. Rendered as a button when `onOtherSellersClick` is
   * supplied, otherwise as static copy.
   */
  otherSellersLabel?: string;
  onOtherSellersClick?: () => void;
}

const DEFAULT_FOOTER = 'Delivery time may vary based on items in your bag';

/**
 * Pure, redux-free Delivery SLA UI for desktop. Renders Single or Multi SLA from
 * a mapped view model. Reusable across desktop PDP and Cart — each page supplies
 * its own data (via a connected container) and callbacks.
 */
const DeliverySLA: FC<DeliverySLAProps> = ({
  viewModel,
  loading = false,
  error = false,
  activePincode,
  onChangePincode,
  onSelectOption,
  footerNote = DEFAULT_FOOTER,
  className = '',
  luxe = false,
  hideHeader = false,
  slaCodeLabels,
  title = null,
  perPiece = false,
  sellerName = '',
  showSellerName = false,
  otherSellersLabel = '',
  onOtherSellersClick
}) => {
  const { options, isMulti, isEmpty } = viewModel;
  const slaClass = cx('sla', className, { luxe, 'sla--dialog': hideHeader });

  const initialSelected = (
    options.find(o => o.isSelected && o.serviceable) || options.find(o => o.serviceable)
  )?.id;

  /**
   * Scope of the cards on screen. Hosts remap the same options into a new
   * viewModel object on every setState (the cart size popup does), so object
   * identity cannot hold the pick — it would snap back to API `isSelected`.
   * Seller + priceType + amount change when the size / pincode does, which is
   * when the API's own selection should take over again.
   */
  const selectionScope = options
    .map(
      option =>
        `${option.id}:${option.sellerId || ''}:${option.rawPriceType || ''}:${option.price
          ?.current || ''}`
    )
    .join('|');

  const [userSelection, setUserSelection] = useState<{
    scope: string;
    id?: string;
  } | null>(null);

  const selectedId =
    userSelection &&
    userSelection.scope === selectionScope &&
    options.some(o => o.id === userSelection.id)
      ? userSelection.id
      : initialSelected;

  const handleSelect = (option: SlaOption) => {
    setUserSelection({ scope: selectionScope, id: option.id });
    onSelectOption?.(option);
  };

  const renderHeader = () => {
    if (hideHeader) return null;
    return (
      <div className={cx('sla-header')}>
        {activePincode && <span className={cx('sla-pincode')}>{activePincode}</span>}
        {onChangePincode && (
          <button type="button" className={cx('sla-change-pincode')} onClick={onChangePincode}>
            Change Pincode
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={slaClass} data-testid="sla-loading">
        {renderHeader()}
        <div className={cx('sla-skeleton')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={slaClass}>
        {title}
        {renderHeader()}
        <p className={cx('sla-error')}>Unable to fetch delivery details. Please try again.</p>
      </div>
    );
  }

  if (isEmpty) return null;

  return (
    <div className={slaClass} role={isMulti ? 'radiogroup' : undefined}>
      {title}
      {renderHeader()}
      <div className={cx('sla-list')}>
        {options.map(option => (
          <SlaOptionCard
            key={option.id}
            option={option}
            selectable={isMulti}
            showBadge={isMulti}
            selected={option.id === selectedId}
            onSelect={handleSelect}
            htmlName="sla-option-group"
            luxe={luxe}
            slaCodeLabels={slaCodeLabels}
            perPiece={perPiece}
            activePincode={activePincode}
            showSellerName={showSellerName}
          />
        ))}
      </div>
      {sellerName && (
        <div className={cx('sla-seller')}>
          <img
            src={luxe ? SELLER_ICON_LUXE : SELLER_ICON}
            alt=""
            aria-hidden="true"
            className={cx('sla-seller-icon')}
          />
          <span className={cx('sla-seller-name')}>{`Seller: ${sellerName}`}</span>
        </div>
      )}
      {otherSellersLabel &&
        (onOtherSellersClick ? (
          <button
            type="button"
            className={cx('sla-other-sellers')}
            onClick={onOtherSellersClick}
          >
            {otherSellersLabel}
          </button>
        ) : (
          <p className={cx('sla-other-sellers')}>{otherSellersLabel}</p>
        ))}
      {footerNote && <p className={cx('sla-footer')}>{footerNote}</p>}
    </div>
  );
};

export default DeliverySLA;
